import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ApiError, apiGet } from "../lib/api";
import { etatCleSchema, reponseCleSchema, reponseSceneSchema } from "./contract";
import {
  ESSAI_ADRESSE_LABEL,
  ESSAI_ADRESSE_NOTE,
  ESSAI_ANNULER,
  ESSAI_CHARGEMENT,
  ESSAI_CLE_ABSENTE,
  ESSAI_CLE_ECHEC,
  ESSAI_CONFIRMATION,
  ESSAI_CONFIRMER,
  ESSAI_GENERATION,
  ESSAI_GENERER,
  ESSAI_LECTURE_ECHEC,
  ESSAI_PAS_DE_SCENE,
  ESSAI_REGENERER,
  ESSAI_TITRE,
  essaiCleTexte,
  essaiSceneTexte,
} from "./regie-texts";
import { CARTE, TITRE_CARTE } from "./reglages/cadre";

/**
 * La carte « Essai d'OBS » de la regie. Elle lit `GET /api/direct/scene` (la derniere scene que l'overlay
 * d'essai a signalee, backend `direct/routes.ts`) et dit laquelle, a quelle heure, et de quel overlay. Elle
 * genere aussi la cle des overlays (`POST /api/direct/cle`) et montre l'adresse complete de l'overlay
 * d'essai, `<origine>/overlay/essai#cle=<cle>`, **une seule fois** : la base n'a que l'empreinte de la cle,
 * et la page ne la garde pas (elle disparait des que la carte se demonte).
 *
 * La scene est relue toutes les 8 secondes, page ouverte, sans repasser par « Lecture... ». Toutes les
 * phrases sont dans `regie-texts.ts` ; rien du serveur n'est affiche que du texte ecrit par l'app.
 */

/** Le delai entre deux relectures de la scene, page ouverte. */
export const ESSAI_REFRESH_MS = 8_000;

const SCENE_KEY = ["direct", "scene"] as const;
const CLE_KEY = ["direct", "cle"] as const;

/** Vrai pour une erreur qui dit que la session est perdue : la relire ne servirait a rien. */
function sessionPerdue(error: Error | null): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

/** La generation de la cle : un POST sans corps. L'origine est celle de l'application, le navigateur l'envoie. */
export async function genererRequete(): Promise<string> {
  const response = await fetch("/api/direct/cle", {
    method: "POST",
    headers: { accept: "application/json" },
  });
  if (!response.ok) {
    throw new ApiError("cle_failed", "Generation en echec", response.status);
  }
  const lue = reponseCleSchema.safeParse(await response.json().catch(() => null));
  if (!lue.success) {
    throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
  }
  return lue.data.cle;
}

export function EssaiObs() {
  const queryClient = useQueryClient();
  // L'adresse complete, montree une seule fois : elle n'est jamais relue de la base, qui n'en a que l'empreinte.
  const [adresse, setAdresse] = useState<string | null>(null);
  // Regenerer coupe tous les overlays deja colles dans OBS : on demande d'abord.
  const [confirmation, setConfirmation] = useState(false);

  const scene = useQuery({
    queryKey: SCENE_KEY,
    queryFn: () => apiGet("/api/direct/scene", reponseSceneSchema),
    retry: false,
    gcTime: 0,
    refetchInterval: (query) => (sessionPerdue(query.state.error) ? false : ESSAI_REFRESH_MS),
  });
  const cle = useQuery({
    queryKey: CLE_KEY,
    queryFn: () => apiGet("/api/direct/cle", etatCleSchema),
    retry: false,
    gcTime: 0,
  });

  const generer = useMutation({
    mutationFn: genererRequete,
    onSuccess: (nouvelle) => {
      setConfirmation(false);
      setAdresse(`${window.location.origin}/overlay/essai#cle=${nouvelle}`);
      void queryClient.invalidateQueries({ queryKey: CLE_KEY });
    },
  });

  function lancer(): void {
    generer.reset();
    setAdresse(null);
    generer.mutate();
  }

  return (
    <section aria-labelledby="essai-obs-titre">
      <Card className={CARTE}>
        <CardHeader>
          <CardTitle id="essai-obs-titre" className={TITRE_CARTE}>
            {ESSAI_TITRE}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 text-[20px]">
          {scene.isPending ? (
            <p className="text-muted-foreground">{ESSAI_CHARGEMENT}</p>
          ) : scene.isError ? (
            <p className="text-destructive">{ESSAI_LECTURE_ECHEC}</p>
          ) : scene.data.scene === null ? (
            <p className="text-muted-foreground">{ESSAI_PAS_DE_SCENE}</p>
          ) : (
            <p>{essaiSceneTexte(scene.data.scene)}</p>
          )}

          {cle.isSuccess ? (
            <p className={cle.data.posee ? undefined : "text-muted-foreground"}>
              {cle.data.posee ? essaiCleTexte(cle.data.creeeLe) : ESSAI_CLE_ABSENTE}
            </p>
          ) : null}
          {confirmation ? (
            <div className="flex flex-col items-start gap-2" role="alert">
              <p>{ESSAI_CONFIRMATION}</p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={generer.isPending} onClick={lancer}>
                  {ESSAI_CONFIRMER}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmation(false)}>
                  {ESSAI_ANNULER}
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button
                size="sm"
                variant="outline"
                disabled={generer.isPending || !cle.isSuccess}
                onClick={() => (cle.isSuccess && cle.data.posee ? setConfirmation(true) : lancer())}
              >
                {cle.isSuccess && cle.data.posee ? ESSAI_REGENERER : ESSAI_GENERER}
              </Button>
            </div>
          )}
          {generer.isPending ? (
            <p className="text-muted-foreground" role="status">
              {ESSAI_GENERATION}
            </p>
          ) : generer.isError ? (
            <p className="text-destructive" role="status">
              {ESSAI_CLE_ECHEC}
            </p>
          ) : null}
          {adresse === null ? null : (
            <div className="flex flex-col gap-1">
              <label htmlFor="essai-obs-adresse" className="font-medium">
                {ESSAI_ADRESSE_LABEL}
              </label>
              <input
                id="essai-obs-adresse"
                readOnly
                value={adresse}
                className="w-full rounded border bg-background px-2 py-1 font-mono text-[18px]"
                onFocus={(event) => event.currentTarget.select()}
              />
              <p className="text-muted-foreground">{ESSAI_ADRESSE_NOTE}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
